import { FadeUp } from "./Motion";
import { TrendingUp, Boxes, Smartphone, Users2 } from "lucide-react";

const pillars = [
  {
    n: "01",
    icon: TrendingUp,
    title: "Financial Health",
    tier: "v1",
    body:
      "Revenue consistency, cash flow analysis, GST compliance, and credit history — the verified financial signal lenders can underwrite against.",
    detail: "Sources: Bank Statement Analysis (Surepass / Account Aggregator), GST Returns (Surepass GST API), Commercial Credit Bureau (CIBIL Commercial Rank).",
    span: "md:col-span-5",
    dark: false,
  },
  {
    n: "02",
    icon: Boxes,
    title: "Operational Health",
    tier: "v1",
    body:
      "Customer diversification, supplier relationships, and verified trading partner networks — how the business actually operates day to day.",
    detail: "Sources: e-Invoice API (Surepass), GSTR-2A Cross-Verification (GST Network).",
    span: "md:col-span-7",
    dark: true,
    highlight: true,
  },
  {
    n: "03",
    icon: Smartphone,
    title: "Digital Presence",
    tier: "v2",
    body:
      "UPI transaction analytics, digital payment trends, and social-commerce activity — an additional lens on how modern MSMEs earn.",
    detail: "V2 Roadmap: Payment gateway integrations (Cashfree, Razorpay), UPI transaction analysis.",
    span: "md:col-span-5",
    dark: false,
  },
  {
    n: "04",
    icon: Users2,
    title: "Business Reputation",
    tier: "v2",
    body:
      "Payment behaviour analysis, invoice-to-payment reconciliation, and PO fulfilment tracking — a verified view of commercial trust.",
    detail: "V2 Roadmap: Accounting software integration (Tally, Zoho Books), invoice payment matching, marketplace verification.",
    span: "md:col-span-7",
    dark: false,
  },
];

export const Pillars = () => {
  return (
    <section id="solution" className="relative py-24 md:py-36 border-t border-ink/10 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className="flex items-center gap-6 mb-16">
            <span className="font-mono-cap text-ink/60">§ 02 — The Solution</span>
            <span className="hair-divider flex-1" />
          </div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <FadeUp>
              <h2 className="font-display text-5xl md:text-7xl text-ink leading-[0.98]">
                A business health report <br />
                built on <span className="italic text-teal">verified data</span>.
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pb-2">
            <FadeUp delay={0.15}>
              <p className="font-sans text-ink/70 leading-relaxed">
                V1 launches with two core pillars powered by verified API
                data sources. Two more pillars are on the V2 roadmap.
              </p>
            </FadeUp>
          </div>
        </div>

        {/* Bento grid */}
        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-8">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeUp
                key={p.n}
                delay={i * 0.08}
                className={`col-span-12 ${p.span}`}
              >
                <div
                  data-testid={`pillar-card-${p.n}`}
                  className={`pillar-card group h-full flex flex-col ${p.dark ? "pillar-card--dark" : ""}`}
                >
                  <div className={`flex items-start justify-between ${p.dark ? "text-paper/70" : "text-ink/60"}`}>
                    <span className="font-mono-cap">Pillar {p.n}</span>
                    {p.tier === "v2" ? (
                      <span
                        data-testid={`v2-badge-${p.n}`}
                        className="font-mono-cap text-ink/70 bg-ink/[0.04] px-3 py-1 border border-ink/25"
                      >
                        Coming in V2
                      </span>
                    ) : p.highlight ? (
                      <span
                        data-testid="differentiator-badge"
                        className="font-mono-cap text-teal bg-teal/10 px-3 py-1 border border-teal/30"
                      >
                        V1 · Live
                      </span>
                    ) : (
                      <span className="font-mono-cap text-teal bg-teal/10 px-3 py-1 border border-teal/30">
                        V1 · Live
                      </span>
                    )}
                  </div>

                  <div className={`mt-10 flex items-center gap-4 ${p.tier === "v2" ? "opacity-70" : ""}`}>
                    <div className={`w-11 h-11 flex items-center justify-center transition-transform duration-500 group-hover:-translate-y-0.5 ${p.dark ? "bg-teal text-paper" : p.tier === "v2" ? "bg-ink/70 text-paper" : "bg-ink text-paper"}`}>
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <h3 className={`font-display text-3xl md:text-4xl leading-tight ${p.dark ? "text-paper" : "text-ink"}`}>
                      {p.title}
                    </h3>
                  </div>

                  <p className={`mt-6 font-sans leading-relaxed ${p.dark ? "text-paper/75" : "text-ink/70"}`}>
                    {p.body}
                  </p>

                  {/* Hover-reveal detail */}
                  <div
                    className={`pillar-detail overflow-hidden transition-[max-height,opacity,margin] duration-500 ease-out max-h-0 opacity-0 mt-0 group-hover:max-h-48 group-hover:opacity-100 group-hover:mt-6 border-t ${p.dark ? "border-paper/20" : "border-ink/10"}`}
                  >
                    <div className={`pt-4 font-mono-cap text-[0.68rem] leading-relaxed ${p.dark ? "text-paper/70" : "text-ink/60"}`}>
                      {p.detail}
                    </div>
                  </div>

                  {p.highlight && (
                    <div className="mt-8 grid grid-cols-3 gap-3 pt-6 border-t border-paper/20">
                      {["GST-Verified", "50+ Metrics", "API-Driven"].map((t) => (
                        <div key={t} className="font-mono-cap text-paper/60 text-[0.65rem]">
                          {t}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>

        {/* V1 / V2 roadmap callout */}
        <FadeUp delay={0.05} className="mt-10 md:mt-14">
          <div className="border border-ink/15 bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            <div className="flex gap-5">
              <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-teal text-paper font-mono-cap">
                V1
              </div>
              <div>
                <div className="font-mono-cap text-teal">V1 Launch · Current</div>
                <p className="font-sans text-ink/80 mt-2 leading-relaxed">
                  Financial Health + Operational Health with verified API data
                  sources — GST Network, CIBIL Commercial, Account Aggregator.
                </p>
              </div>
            </div>
            <div className="flex gap-5 md:border-l md:border-ink/15 md:pl-10">
              <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-ink/85 text-paper font-mono-cap">
                V2
              </div>
              <div>
                <div className="font-mono-cap text-ink/70">V2 Roadmap · Coming Soon</div>
                <p className="font-sans text-ink/70 mt-2 leading-relaxed">
                  Digital transaction analytics and payment-behaviour analysis
                  via payment-gateway and accounting-software integrations.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
